/**
 * API Client Factory
 * Creates API clients with credentials enabled for cookie-based authentication
 * and provides utilities for consistent error handling.
 */

import { API_CONFIG } from '../config/api';
import { ERROR_MESSAGES } from '../constants/messages';

// ============================================
// Fetch Configuration
// ============================================

// Custom fetch wrapper that includes credentials
const fetchWithCredentials = (url: RequestInfo, init?: RequestInit): Promise<Response> => {
  return fetch(url, {
    ...init,
    credentials: 'include', // Include cookies in requests
  });
};

// ============================================
// API Client Factory
// ============================================

/**
 * Creates an API client with credentials enabled
 * Usage: const client = createApiClient(UsersClient);
 */
export function createApiClient<T>(ClientClass: new (baseUrl?: string, http?: any) => T): T {
  return new ClientClass(API_CONFIG.baseUrl, { fetch: fetchWithCredentials });
}

// ============================================
// API Call Wrapper with Error Handling
// ============================================

export interface ApiCallOptions {
  /** Custom error message to log/display on failure */
  errorMessage?: string;
  /** Whether to rethrow the error after handling (default: true) */
  rethrow?: boolean;
  /** Custom error handler */
  onError?: (error: unknown) => void;
}

/**
 * Wraps an API call with consistent error handling
 *
 * @example
 * // Basic usage
 * const employees = await apiCall(() => employeesClient.getEmployees());
 *
 * // With custom error message
 * const employee = await apiCall(
 *   () => employeesClient.getEmployee(id),
 *   { errorMessage: 'Failed to load employee details' }
 * );
 *
 * // With custom error handler
 * const result = await apiCall(
 *   () => client.saveData(data),
 *   {
 *     errorMessage: 'Save failed',
 *     onError: (e) => toast.error('Unable to save changes'),
 *     rethrow: false
 *   }
 * );
 */
export async function apiCall<T>(
  operation: () => Promise<T>,
  options: ApiCallOptions = {}
): Promise<T> {
  const { errorMessage, rethrow = true, onError } = options;

  try {
    return await operation();
  } catch (error) {
    // Log error with context
    console.error(errorMessage || ERROR_MESSAGES.GENERIC, error);

    // Call custom error handler if provided
    if (onError) {
      onError(error);
    }

    // Rethrow if requested
    if (rethrow) {
      throw error;
    }

    // Return undefined when not rethrowing (caller must handle this case)
    return undefined as T;
  }
}

// ============================================
// Resource-specific API call helpers
// ============================================

/**
 * Helper for fetch operations with standardized error message
 */
export async function fetchResource<T>(
  operation: () => Promise<T>,
  resourceName: string,
  options: Omit<ApiCallOptions, 'errorMessage'> = {}
): Promise<T> {
  return apiCall(operation, {
    ...options,
    errorMessage: ERROR_MESSAGES.FETCH_FAILED(resourceName),
  });
}

/**
 * Helper for save operations with standardized error message
 */
export async function saveResource<T>(
  operation: () => Promise<T>,
  resourceName: string,
  options: Omit<ApiCallOptions, 'errorMessage'> = {}
): Promise<T> {
  return apiCall(operation, {
    ...options,
    errorMessage: ERROR_MESSAGES.SAVE_FAILED(resourceName),
  });
}

/**
 * Helper for delete operations with standardized error message
 */
export async function deleteResource<T>(
  operation: () => Promise<T>,
  resourceName: string,
  options: Omit<ApiCallOptions, 'errorMessage'> = {}
): Promise<T> {
  return apiCall(operation, {
    ...options,
    errorMessage: ERROR_MESSAGES.DELETE_FAILED(resourceName),
  });
}

/**
 * Helper for update operations with standardized error message
 */
export async function updateResource<T>(
  operation: () => Promise<T>,
  resourceName: string,
  options: Omit<ApiCallOptions, 'errorMessage'> = {}
): Promise<T> {
  return apiCall(operation, {
    ...options,
    errorMessage: ERROR_MESSAGES.UPDATE_FAILED(resourceName),
  });
}
